import { message } from 'antd'
import { nextRoiVisibleUserIds } from 'features/libra/dashboard/canViewExecutiveRoi'
import {
  type LibraDashboardConfig,
  useDashboardConfigs,
  useUpdateDashboardConfig,
} from 'features/libra/dashboard/libraDashboardApi'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useState } from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

export function useToggleRoiVisible(config: LibraDashboardConfig): {
  isGeckoInternal: boolean
  isOptedIn: boolean
  saving: boolean
  setVisible: (show: boolean) => Promise<void>
} {
  const { user, hasPermissions } = useGeckoPermissions()
  const { forceRefresh } = useDashboardConfigs({ tags: config.tags })
  const updateConfig = useUpdateDashboardConfig()
  const [saving, setSaving] = useState(false)

  const isGeckoInternal = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const userId = user?.id
  const isOptedIn =
    userId !== undefined &&
    (config.roiVisibleUserIds?.includes(userId) ?? false)

  const setVisible = useCallback(
    async (show: boolean) => {
      if (!userId || !isGeckoInternal) return
      setSaving(true)
      try {
        await updateConfig({
          config,
          roiVisibleUserIds: nextRoiVisibleUserIds(
            config.roiVisibleUserIds,
            userId,
            show
          ),
        })
        await forceRefresh()
      } catch {
        message.error('Could not update ROI visibility')
      } finally {
        setSaving(false)
      }
    },
    [config, forceRefresh, isGeckoInternal, updateConfig, userId]
  )

  return { isGeckoInternal, isOptedIn, saving, setVisible }
}
